import type { TraceStructuredFilterGroupRequest } from "./useTracesSearch";

import { ENDPOINTS } from "@/api/application-api";
import { queryKeys } from "@/constants";
import { TraceListItem } from "@/types/traces";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

export interface TracesSearchParams {
  filterGroup?: TraceStructuredFilterGroupRequest | null;
  sortField?: string;
  sortOrder?: "asc" | "desc";
}

export interface TracesSearchPageResponse {
  success: boolean;
  items: TraceListItem[];
  page: number;
  per_page: number;
  total: number;
  total_is_lower_bound: boolean;
  next_after_key: Record<string, unknown> | null;
  pagination_mode: "page" | "cursor";
}

interface FetchTracesSearchPaginatedArgs {
  signal?: AbortSignal;
  userToken: string;
  workspaceId: number;
  page: number;
  pageSize: number;
  searchParams: TracesSearchParams;
  afterKey?: Record<string, unknown> | null;
}

export const getTracesSearchPaginatedQueryKeyPrefix = (userToken: string, workspaceId: number) =>
  [queryKeys.traces, "search-paginated", userToken, workspaceId] as const;

export const getTracesSearchPaginatedQueryKey = ({
  userToken,
  workspaceId,
  page,
  pageSize,
  searchParams,
  afterKey,
}: FetchTracesSearchPaginatedArgs) =>
  [
    ...getTracesSearchPaginatedQueryKeyPrefix(userToken, workspaceId),
    page,
    pageSize,
    searchParams,
    afterKey ?? null,
  ] as const;

export const fetchTracesSearchPaginated = async ({
  signal,
  userToken,
  workspaceId,
  page,
  pageSize,
  searchParams,
  afterKey,
}: FetchTracesSearchPaginatedArgs): Promise<TracesSearchPageResponse> => {
  const body: Record<string, unknown> = {
    filter_group: searchParams.filterGroup ?? null,
    page,
    per_page: pageSize,
    after_key: afterKey ?? null,
  };
  if (searchParams.sortField) {
    body.sort_field = searchParams.sortField;
  }
  if (searchParams.sortOrder) {
    body.sort_order = searchParams.sortOrder;
  }

  const response = await fetch(ENDPOINTS.traces_search(workspaceId), {
    method: "POST",
    headers: { ...authHeader(userToken), "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
    signal,
  });

  return handleResponse(response);
};
